import type { components } from "./schema";

type ErrorBody = components["schemas"]["ErrorBody"];

/** A failed API call, carrying the backend's stable error `code`. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: string | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(status: number, body: ErrorBody, retryAfterSeconds?: number) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.field = body.field ?? undefined;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Builds an error from a response, tolerating non-envelope bodies. */
  static async fromResponse(response: Response): Promise<ApiError> {
    const retry = response.headers.get("retry-after");
    const retryAfter = retry !== null && /^\d+$/.test(retry) ? Number(retry) : undefined;
    let body: ErrorBody = { code: "unknown", message: response.statusText || "Request failed" };
    try {
      const parsed: unknown = await response.clone().json();
      if (isEnvelope(parsed)) body = parsed.error;
    } catch {
      // Non-JSON body (proxy error page); keep the generic message.
    }
    return new ApiError(response.status, body, retryAfter);
  }
}

/** Network failure before any HTTP response was received. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Network request failed", { cause });
    this.name = "NetworkError";
  }
}

function isEnvelope(value: unknown): value is { error: ErrorBody } {
  if (typeof value !== "object" || value === null || !("error" in value)) return false;
  const error = value.error;
  return (
    typeof error === "object" &&
    error !== null &&
    typeof (error as { code?: unknown }).code === "string" &&
    typeof (error as { message?: unknown }).message === "string"
  );
}
